import React, { useState, useEffect, useCallback } from 'react';
import { IoNotifications } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import fetchData from 'Utilis/request';
import { useReadArticles } from '../hooks/useReadArticles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Article {
  id: string;
  post_title: string;
  post_featured_image: string;
  created: string;
  href: string;
  type: string;
}

const NotificationDropdown: React.FC<{ darkMode?: boolean }> = ({ darkMode = false }) => {
  const { checkIfArticleRead } = useReadArticles();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // ✅ Memoized fetch function
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetchData.getResult(`${API_BASE_URL}getArticleLatest.php`);
      const data = response.articles || [];
      setArticles(data);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  }, []);

  // Auto-fetch on mount and every 5 mins
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]); // useCallback makes this safe

  const handleClick = () => {
    setShowDropdown(!showDropdown);
  };

  const openNotification = (url: string) => {
    setShowDropdown(false);
    navigate(url);
  };

  const unreadArticles = articles.filter(
    (article) => !checkIfArticleRead(article.id, article.type)
  );

  return (
    <div className="flex relative">
      <button
        onClick={handleClick}
        className={`relative rounded-button ${darkMode ? 'text-white' : 'text-gray-800'}`}
        aria-label="Notifications"
      >
        <IoNotifications size={24} />
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
          {unreadArticles.length > 0 ? `${unreadArticles.length > 9 ? '9+' : unreadArticles.length}` : '0'}
        </span>
      </button>

      {showDropdown && (
        <div className={`absolute right-0 mt-8 w-80 border rounded-lg shadow-lg z-50 ${darkMode ? 'bg-gray-800' : 'bg-white'} border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="test-close fixed inset-0 bg-gray-900/50" onClick={handleClick}></div>
          <div className={`rounded-lg z-10 relative ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-3 border-b font-semibold text-sm ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              Latest News
            </div>
            <div className="max-h-96 overflow-y-auto">
              {unreadArticles.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No unread notifications.</div>
              ) : (
                unreadArticles.slice(0, 10).map((article) => (
                  <button
                    key={article.id}
                    onClick={() => openNotification(`/${article.href}`)}
                    className={`flex gap-3 p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
                  >
                    <img
                      src={article.post_featured_image}
                      alt={article.post_title}
                      className="w-14 h-14 rounded object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-small text-base line-clamp-2 text-start">{article.post_title}</p>
                      <p className="text-xs text-gray-500 mt-1 text-start">{article.created}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
